import { useState, useEffect } from "react";
import { get_file_from_s3 } from "../../utils";
import { useGetOrganizationChartQuery } from "../../api/organizationChartApi";
import { useGetAllTenantsQuery } from "../../api/tenantApi";
import { getToken } from "../../services/Token";
import "./OrgChart.css";

const ROLE_COLORS = {
  ADMIN: "#7c3aed",
  PROXY_ADMIN: "#0891b2",
  MANAGER: "#2563eb",
  EMPLOYEE: "#059669",
};

const getInitials = (name) => {
  if (!name) return " ";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].substring(0, 2).toUpperCase();
};

const formatRole = (role) => {
  if (!role) return "";
  return role
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const NodeCard = ({ node }) => {
  const color = ROLE_COLORS[node.role] || "#6b7280";
  const [photoUrl, setPhotoUrl] = useState("");

  useEffect(() => {
    const s3FileName = node.profilePhoto?.s3Document?.fileName;
    if (s3FileName) {
      get_file_from_s3(s3FileName).then((url) => setPhotoUrl(url || ""));
    } else {
      setPhotoUrl(node.profilePhoto?.resourceName?.trim() || "");
    }
  }, [node.profilePhoto]);

  return (
    <>
      <div className="orgchart-card">
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={node.name}
            className="orgchart-avatar orgchart-avatar--photo"
          />
        ) : (
          <div className="orgchart-avatar" style={{ backgroundColor: color }}>
            {getInitials(node.name)}
          </div>
        )}
        <p className="orgchart-emp-name">{node.name}</p>
        <p className="orgchart-emp-title">
          {node.designationName || node.departmentName || ""}
        </p>
        <span
          className="orgchart-role-badge"
          style={{ backgroundColor: color + "20", color }}
        >
          {formatRole(node.role)}
        </span>
      </div>

      {/* Hover tooltip */}
      <div className="orgchart-tooltip">
        <p className="orgchart-tooltip-name">{node.name}</p>
        <div className="orgchart-tooltip-row">
          <span className="orgchart-tooltip-label">Email</span>
          <span className="orgchart-tooltip-value">{node.emailId}</span>
        </div>
        {node.departmentName && (
          <div className="orgchart-tooltip-row">
            <span className="orgchart-tooltip-label">Department</span>
            <span className="orgchart-tooltip-value">{node.departmentName}</span>
          </div>
        )}
        {node.designationName && (
          <div className="orgchart-tooltip-row">
            <span className="orgchart-tooltip-label">Designation</span>
            <span className="orgchart-tooltip-value">{node.designationName}</span>
          </div>
        )}
      </div>
    </>
  );
};

const OrgChartNode = ({ node }) => {
  const hasChildren = node.children && node.children.length > 0;
  const proxyAdmins = node.proxyAdmins || [];
  const hasProxyAdmins = proxyAdmins.length > 0;

  return (
    <div className="orgchart-node-container">
      <div className="orgchart-card-wrapper">
        <NodeCard node={node} />

        {hasProxyAdmins && (
          <div className="orgchart-proxy-admin-row">
            {proxyAdmins.map((proxy) => (
              <>
                <div key={`connector-${proxy.employeeId}`} className="orgchart-proxy-connector" />
                <div key={proxy.employeeId} className="orgchart-card-wrapper">
                  <NodeCard node={proxy} />
                </div>
              </>
            ))}
          </div>
        )}
      </div>

      {hasChildren && (
        <>
          <div className="orgchart-line-down" />
          <div className="orgchart-children-row">
            {node.children.map((child) => (
              <div key={child.employeeId} className="orgchart-col">
                <OrgChartNode node={child} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const TenantDropdown = ({ tenants, selectedTenantId, onChange }) => (
  <div className="orgchart-tenant-selector">
    <label className="orgchart-tenant-label">Select Tenant</label>
    <select
      className="orgchart-tenant-select"
      value={selectedTenantId}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">-- Select a tenant --</option>
      {tenants.map((t) => (
        <option key={t.tenantId} value={t.tenantId}>
          {t.tenantName}
        </option>
      ))}
    </select>
  </div>
);

const OrganizationChart = () => {
  const role = getToken("roles");
  const isSuperAdmin = role === "SUPERADMIN";

  const loggedInTenantId = getToken("userId");
  const [selectedTenantId, setSelectedTenantId] = useState("");

  const tenantId = isSuperAdmin ? selectedTenantId : loggedInTenantId;

  const { data: tenantsData, isLoading: tenantsLoading } =
    useGetAllTenantsQuery(undefined, { skip: !isSuperAdmin });

  const tenants = Array.isArray(tenantsData) ? tenantsData : [];

  const { data, isLoading, isError } = useGetOrganizationChartQuery(
    { tenantId },
    { skip: !tenantId }
  );

  const treeData = data?.data || [];

  return (
    <div className="orgchart-wrapper">
      <h1 className="orgchart-page-title">Organization Chart</h1>

      {isSuperAdmin && (
        <TenantDropdown
          tenants={tenants}
          selectedTenantId={selectedTenantId}
          onChange={setSelectedTenantId}
        />
      )}

      {isSuperAdmin && !selectedTenantId && (
        <div className="orgchart-state">
          Select a tenant above to view its organization chart.
        </div>
      )}

      {(isLoading || (isSuperAdmin && tenantsLoading)) && tenantId && (
        <div className="orgchart-state">Loading organization chart...</div>
      )}

      {isError && (
        <div className="orgchart-state orgchart-state--error">
          Failed to load the organization chart. Please try again.
        </div>
      )}

      {!isLoading && !isError && tenantId && !treeData.length && (
        <div className="orgchart-state">No data available for this tenant.</div>
      )}

      {!isLoading && !isError && treeData.length > 0 && (
        <div className="orgchart-scroll-container">
          <div className="orgchart-root">
            {treeData.map((rootNode) => (
              <OrgChartNode key={rootNode.employeeId} node={rootNode} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationChart;
